import React from 'react';
import Icon from '@components/AppIcon';
import Button from '@components/ui/Button';
import Footer from '@components/ui/Footer';

const SocialMediaLinks = () => {
  const socialPlatforms = [
    {
      name: 'Facebook',
      icon: 'Facebook',
      handle: '@EyeUKenya',
      description: 'Follow us for eye health tips, patient stories, and clinic updates',
      followers: '1.4K followers',
      url: 'https://facebook.com/eyenukenyaofficial',
      color: 'bg-blue-50 text-blue-600 border-blue-200'
    },
    {
      name: 'Instagram',
      icon: 'Instagram',
      handle: '@eyeukenya',
      description: 'Visual stories of patient transformations and behind-the-scenes content',
      followers: '2.5K followers',
      url: 'https://instagram.com/eyenu.hospital',
      color: 'bg-pink-50 text-pink-600 border-pink-200'
    },
    {
      name: 'Twitter',
      icon: 'Twitter',
      handle: '@EyeUKenya',
      description: 'Real-time updates, health awareness campaigns, and community engagement',
      followers: '5.8K followers',
      url: 'https://x.com/eyenu_ke',
      color: 'bg-sky-50 text-sky-600 border-sky-200'
    },
    {
      name: 'LinkedIn',
      icon: 'Linkedin',
      handle: 'Eye&U Kenya',
      description: 'Professional network for healthcare partnerships and industry insights',
      followers: '3.1K followers',
      url: 'https://www.linkedin.com/company/eye-n-u-kenya',
      color: 'bg-blue-50 text-blue-700 border-blue-200'
    },
    {
      name: 'YouTube',
      icon: 'Youtube',
      handle: 'Eye&U Kenya Hospital',
      description: 'Educational videos, procedure explanations, and patient testimonials',
      followers: '15.3K subscribers',
      url: 'https://www.youtube.com/@EyenuKenyaHospital',
      color: 'bg-red-50 text-red-600 border-red-200'
    },
    {
      name: 'TikTok',
      icon: 'Music',
      handle: '@eyenu.ke',
      description: 'Quick eye health tips and fun educational content for younger audiences',
      followers: '167 followers',
      url: 'https://www.tiktok.com/@eyenu.ke',
      color: 'bg-gray-50 text-gray-600 border-gray-200'
    }
  ];

  const handleSocialClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="py-16 bg-muted">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-text-primary mb-4" style={{ color: "#08005f" }}>
            Connect With Us on Social Media
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Stay connected with Eye&U Kenya across all platforms. Get the latest eye health tips, 
            patient success stories, and important updates from our medical team.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {socialPlatforms.map((platform) => (
            <div
              key={platform.name}
              className={`${platform.color} rounded-xl p-6 border hover:shadow-lg medical-transition group cursor-pointer`}
              onClick={() => handleSocialClick(platform.url)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center group-hover:scale-110 medical-transition">
                    <Icon name={platform.icon} size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg">
                      {platform.name}
                    </h3>
                    <p className="text-sm opacity-80">
                      {platform.handle}
                    </p>
                  </div>
                </div>
                <Icon name="ExternalLink" size={16} className="opacity-60 group-hover:opacity-100 medical-transition" />
              </div>
              
              <p className="text-sm mb-4 leading-relaxed">
                {platform.description}
              </p>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {platform.followers}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-current hover:bg-white/50"
                >
                  Follow
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Social Media Feed Preview */}
        <div className="bg-white rounded-xl medical-shadow p-8">
          <div className="text-center mb-8">
            <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">
              Latest from Our Social Media
            </h3>
            <p className="text-text-secondary">
              Recent posts and updates from across our social platforms
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Facebook Post */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center">
                  <Icon name="Facebook" size={16} />
                </div>
                <div>
                  <p className="font-medium text-text-primary text-sm">Eye&U Kenya</p>
                  <p className="text-xs text-text-secondary">2 hours ago</p>
                </div>
              </div>
              <p className="text-sm text-text-primary mb-3">
                "Regular eye exams can detect diabetes before symptoms appear. Book your comprehensive 
                eye screening today and take control of your health! 👁️ #EyeHealth #DiabetesAwareness"
              </p>
              <div className="flex items-center space-x-4 text-xs text-text-secondary">
                <span>24 likes</span>
                <span>5 comments</span>
                <span>2 shares</span>
              </div>
            </div>

            {/* Instagram Post */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-pink-50 text-pink-600 rounded-full flex items-center justify-center">
                  <Icon name="Instagram" size={16} />
                </div>
                <div>
                  <p className="font-medium text-text-primary text-sm">@eyeukenya</p>
                  <p className="text-xs text-text-secondary">5 hours ago</p>
                </div>
              </div>
              <div className="bg-muted rounded-lg h-32 mb-3 flex items-center justify-center">
                <Icon name="Image" size={24} className="text-text-secondary" />
              </div>
              <p className="text-sm text-text-primary mb-3">
                "Meet Dr. Sarah Kimani, our pediatric eye specialist! She's helped over 500 children 
                see clearly this year. 👶👁️ #PediatricEyeCare #TeamSpotlight"
              </p>
              <div className="flex items-center space-x-4 text-xs text-text-secondary">
                <span>89 likes</span>
                <span>12 comments</span>
              </div>
            </div>

            {/* YouTube Video */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-red-50 text-red-600 rounded-full flex items-center justify-center">
                  <Icon name="Youtube" size={16} />
                </div>
                <div>
                  <p className="font-medium text-text-primary text-sm">Eye&U Kenya</p>
                  <p className="text-xs text-text-secondary">1 day ago</p>
                </div>
              </div>
              <div className="bg-muted rounded-lg h-32 mb-3 flex items-center justify-center relative">
                <Icon name="Play" size={32} className="text-text-secondary" />
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                  3:45
                </div>
              </div>
              <p className="text-sm text-text-primary mb-3">
                "LASIK Surgery: What to Expect - Complete Guide"
              </p>
              <div className="flex items-center space-x-4 text-xs text-text-secondary">
                <span>1.2K views</span>
                <span>45 likes</span>
                <span>8 comments</span>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <Button
              variant="outline"
              iconName="ExternalLink"
              iconPosition="right"
              onClick={() => window.open('https://linktr.ee/eyeukenya', '_blank')}
            >
              View All Social Media
            </Button>
          </div>
        </div>
      </div>
    </section>

  );
};

export default SocialMediaLinks;